/**
 * Utility functions for working with keyframe timing and fraction interpolation.
 *
 * TypeScript port of C++ KeyframeFraction_Utils from
 * tabanim/public/animation/KeyframeFraction_Utils.h
 */

export interface KeyframeBase {
  fraction: number;
}

/**
 * Utility class with static methods for keyframe fraction calculations.
 */
export class KeyframeFractionUtils {
  /**
   * Returns the index of the first keyframe that falls at or before the given fraction,
   * where the following keyframe falls after the given fraction.
   *
   * @param keyframes Array of keyframes sorted by fraction
   * @param fraction The fraction to search for [0, 1]
   * @returns Index of keyframe at or before fraction, or -1 if fraction is before first keyframe
   */
  public static getFractionArrayIndexBeforeOrAt<T extends KeyframeBase>(keyframes: T[], fraction: number): number {
    if (keyframes.length === 0) {
      return 0;
    }

    if (fraction < keyframes[0].fraction) {
      return -1;
    }

    for (let i = 0; i < keyframes.length; i++) {
      if (i === keyframes.length - 1) {
        return i;
      }

      const curFraction = keyframes[i].fraction;
      const nextFraction = keyframes[i + 1].fraction;

      if (fraction >= curFraction && fraction < nextFraction) {
        return i;
      }
    }

    return keyframes.length - 1;
  }

  /**
   * Maps a fraction [0, 1] to a [0, 1] value within the interval between two consecutive keyframes.
   *
   * The returned value represents the position within the segment:
   * (fraction - keyframes[index0].fraction) / (keyframes[index1].fraction - keyframes[index0].fraction)
   *
   * @param keyframes Array of keyframes sorted by fraction
   * @param fraction The current overall fraction [0, 1]
   * @param index0 Index of first keyframe in segment
   * @param index1 Index of second keyframe in segment (must be index0 + 1)
   * @returns Value in [0, 1] representing position within the segment, or 0 if invalid indices
   */
  public static mapFractionToInterval<T extends KeyframeBase>(keyframes: T[], fraction: number, index0: number, index1: number): number {
    if (index0 < 0 || index1 !== index0 + 1) {
      return 0;
    }

    if (index1 >= keyframes.length) {
      return 0;
    }

    const fraction0 = keyframes[index0].fraction;
    const fraction1 = keyframes[index1].fraction;

    if (fraction <= fraction0) {
      return 0;
    }
    if (fraction >= fraction1) {
      return 1;
    }

    return (fraction - fraction0) / (fraction1 - fraction0);
  }
}
